import { useState } from 'react';
import { api, ApiError } from '../api/client';
import type { DashboardContext } from '../api/types';
import { c, radius } from '../theme/tokens';
import { Mono } from './primitives';

const PROMPTS = [
  'What needs my attention?',
  'Why were callers frustrated?',
  'Summarise the last 7 days',
];

/** A tenant-scoped operational guide, never a remote control for live calls. */
export function AskVoiceProp({ ctx }: { ctx: DashboardContext }) {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const ask = async (value = question) => {
    const q = value.trim();
    if (!q || busy) return;
    setQuestion(q); setBusy(true); setError(null);
    try {
      const result = await api.askVoiceProp(q, ctx.branch.id);
      setAnswer(result.answer);
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Ask VoiceProp could not answer right now.'); }
    finally { setBusy(false); }
  };

  return <>
    {open ? <section className="vp-ask-panel" aria-label="Ask VoiceProp">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div><Mono size={10} tracking={0.12} color={c.sky}>ASK VOICEPROP</Mono><div style={{ fontSize: 17, marginTop: 5, color: c.surface2 }}>Your agency operations guide</div></div>
        <button className="vp-reset" onClick={() => setOpen(false)} style={{ color: 'rgba(255,255,255,.72)', fontSize: 19, padding: 4 }} aria-label="Close Ask VoiceProp">×</button>
      </div>
      <div style={{ fontSize: 13.5, lineHeight: 1.5, color: 'rgba(255,255,255,.72)' }}>Ask about your calls, queue and trends. VoiceProp explains, it does not change live settings.</div>
      {answer ? <div style={{ background: 'rgba(255,255,255,.1)', borderRadius: radius.button, padding: '14px', fontSize: 14, lineHeight: 1.55, color: c.surface2, whiteSpace: 'pre-wrap' }}>{answer}</div> : null}
      {error ? <div style={{ color: '#ffd1d7', fontSize: 13 }}>{error}</div> : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>{PROMPTS.map(p => <button key={p} className="vp-reset" onClick={() => ask(p)} style={{ border: '1px solid rgba(255,255,255,.22)', borderRadius: radius.pill, padding: '7px 10px', color: c.surface2, fontSize: 12 }}>{p}</button>)}</div>
      <form onSubmit={e => { e.preventDefault(); void ask(); }} style={{ display: 'flex', gap: 8 }}>
        <input value={question} onChange={e => setQuestion(e.target.value)} maxLength={1200} placeholder="Ask about your calls…" style={{ flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: radius.button, border: '1px solid rgba(255,255,255,.25)', background: 'rgba(255,255,255,.1)', color: c.surface2, fontSize: 14 }} />
        <button className="vp-reset" disabled={busy || !question.trim()} style={{ padding: '10px 13px', borderRadius: radius.button, background: busy ? 'rgba(255,255,255,.18)' : c.sky, color: c.ink, fontSize: 13, opacity: !question.trim() ? .5 : 1 }}>{busy ? '…' : 'Ask'}</button>
      </form>
    </section> : null}
    <button className="vp-reset vp-ask-fab" onClick={() => setOpen(v => !v)} aria-expanded={open}>
      <span>✦</span> Ask VoiceProp
    </button>
  </>;
}
